import { err, ok, type Result } from 'neverthrow';
import { z } from 'zod';

import { zeroPadLeft } from '../pad.js';
import { monthNumberToName } from './helpers.js';
import { NaiveDate } from './naive_date.js';

const separator = '-';

declare const yearMonthBrand: unique symbol;

/**
 * Branded string type for a calendar month.
 *
 * The canonical format is `"YYYY-MM"`. Operations live on the `YearMonth`
 * namespace value below; the branded primitive is naturally usable as a
 * `Map`/`Set` key and sorts chronologically under plain string comparison.
 */
export type YearMonth = string & { readonly [yearMonthBrand]: 'YearMonth' };

const YEAR_MONTH_PATTERN = /^(\d{4})-?(\d{2})$/;

function format(year: number, month: number): YearMonth {
  return `${zeroPadLeft(year, 4)}${separator}${zeroPadLeft(month, 2)}` as YearMonth;
}

/** Normalizes a (year, 1-based month) pair, carrying month over/underflow into the year. */
function fromYearAndMonthNumber(year: number, month: number): YearMonth {
  const totalMonths = year * 12 + (month - 1);
  const normalizedYear = Math.floor(totalMonths / 12);
  const normalizedMonth = totalMonths - normalizedYear * 12 + 1;
  return format(normalizedYear, normalizedMonth);
}

export const YearMonth = {
  // ----- Smart constructors -----

  /**
   * Parse and validate a string in `"YYYY-MM"` or `"YYYYMM"` form, normalizing
   * to the canonical `"YYYY-MM"`. Use this for values coming from outside the
   * type-safe layer (network, URL, user input).
   */
  parse(value: string): Result<YearMonth, string> {
    const match = YEAR_MONTH_PATTERN.exec(value);
    if (match === null) {
      return err(`Invalid YearMonth string (expected "YYYY-MM"): ${value}`);
    }
    const month = Number(match[2]);
    if (month < 1 || month > 12) {
      return err(`Invalid YearMonth: month out of range in ${value}`);
    }
    return ok(`${match[1]}${separator}${match[2]}` as YearMonth);
  },

  /**
   * Cast a string to `YearMonth` without validation. Use only for values that
   * are provably already in the canonical `"YYYY-MM"` format.
   */
  unsafeFromString(value: string): YearMonth {
    return value as YearMonth;
  },

  /**
   * Type guard for a valid canonical `"YYYY-MM"` value: the extended shape plus
   * an in-range month. Distinguishes a `YearMonth` from other branded date
   * strings (`NaiveDate` `"YYYY-MM-DD"`, `BookYear` `"YYYYMMDD-YYYYMMDD"`) in a
   * union, and backs the `schema`/`decoder` boundary checks.
   */
  isYearMonth(value: string): value is YearMonth {
    if (!/^\d{4}-\d{2}$/.test(value)) {
      return false;
    }
    const month = Number(value.slice(5, 7));
    return month >= 1 && month <= 12;
  },

  fromNaiveDate(date: NaiveDate): YearMonth {
    return format(NaiveDate.year(date), NaiveDate.month(date));
  },

  fromYearAndMonth(year: number, month: number): YearMonth {
    return fromYearAndMonthNumber(year, month);
  },

  // ----- Field accessors -----

  year(value: YearMonth): number {
    return Number(value.slice(0, 4));
  },

  monthNumber(value: YearMonth): number {
    return Number(value.slice(5, 7));
  },

  /** First day of the month as a JS `Date` in the local timezone. */
  first(value: YearMonth): Date {
    return new Date(YearMonth.year(value), YearMonth.monthNumber(value) - 1, 1);
  },

  /** Last day of the month as a JS `Date` in the local timezone. */
  last(value: YearMonth): Date {
    return new Date(YearMonth.year(value), YearMonth.monthNumber(value), 0);
  },

  // ----- Comparisons -----

  /** Chronological ordering; the canonical format makes this a string comparison. */
  compare(a: YearMonth, b: YearMonth): number {
    if (a < b) {
      return -1;
    }
    if (a > b) {
      return 1;
    }
    return 0;
  },

  /** Signed number of months from `value` to `other`. */
  monthsUntil(value: YearMonth, other: YearMonth): number {
    const years = YearMonth.year(other) - YearMonth.year(value);
    const months = YearMonth.monthNumber(other) - YearMonth.monthNumber(value);
    return years * 12 + months;
  },

  /** True when `next` is the month immediately after `value` (`2019-12` → `2020-01`). */
  isSequential(value: YearMonth, next: YearMonth): boolean {
    return YearMonth.next(value) === next;
  },

  // ----- Period operations -----

  /** The month after `value` (`2019-12` → `2020-01`). */
  next(value: YearMonth): YearMonth {
    return fromYearAndMonthNumber(YearMonth.year(value), YearMonth.monthNumber(value) + 1);
  },

  nextNth(value: YearMonth, n: number): YearMonth {
    return fromYearAndMonthNumber(YearMonth.year(value), YearMonth.monthNumber(value) + n);
  },

  /** The month before `value` (`2020-01` → `2019-12`). */
  previous(value: YearMonth): YearMonth {
    return fromYearAndMonthNumber(YearMonth.year(value), YearMonth.monthNumber(value) - 1);
  },

  previousNth(value: YearMonth, n: number): YearMonth {
    return fromYearAndMonthNumber(YearMonth.year(value), YearMonth.monthNumber(value) - n);
  },

  /** Shift the year by `increment` (may be negative). */
  updateYear(value: YearMonth, increment: number): YearMonth {
    return fromYearAndMonthNumber(YearMonth.year(value) + increment, YearMonth.monthNumber(value));
  },

  // ----- Display -----

  display(value: YearMonth): string {
    return value;
  },

  displayHumanReadable(value: YearMonth): string {
    return `${monthNumberToName(YearMonth.monthNumber(value))} ${YearMonth.year(value)}`;
  },

  /** Lowercase Swedish three-letter month abbreviation (`"jan"`, `"maj"`, `"okt"`, …). */
  displayShortMonth(value: YearMonth): string {
    return monthNumberToName(YearMonth.monthNumber(value)).slice(0, 3);
  },

  // ----- Coders (cf. simple_id.ts) -----

  /**
   * Zod schema for the canonical `"YYYY-MM"` form: validates and narrows the
   * input string to `YearMonth` without reallocating it. Rejects the basic
   * `"YYYYMM"` form — use `parse` for inputs that need normalizing.
   */
  schema: z.string().refine((value): value is YearMonth => YearMonth.isYearMonth(value), {
    message: 'Invalid YearMonth (expected "YYYY-MM")',
  }),
};
